import { AI_MODELS } from '../constants/aiModels';
import { getFirstUtmFromStorage } from './analytics';
import { trackAi30Event } from './eventTracker';
import { FESTA_COUPON_CODE, FESTA_PHASES, getFestaPhase } from './festaCampaign';

export const AI30_COUPON_CODE = 'AI30';
export const AI30_CAMPAIGN = 'b2g_school_adoption_2026q3';
export const AI30_PENDING_COUPON_KEY = 'pending_coupon_code';
export const AI30_GRANT_TOAST_KEY = 'ai30_grant_toast_shown';
export const AI30_LANDING_PATH = '/events/2nd-semester-ai';
export const AI30_SCHOOL_LEVEL_KEY = 'whatquiz_ai30_school_level';
export const AI30_ARRIVAL_FLAG = 'whatquiz_ai30_arrival_tracked';
export const AI30_FIRST_TASK_FLAG = 'whatquiz_ai30_first_task_tracked';

export const AI30_SCHOOL_LEVELS = [
  { id: 'elementary', label: '초등' },
  { id: 'middle', label: '중등' },
  { id: 'high', label: '고등' },
  { id: 'other', label: '그 외' },
];

// model은 실제 제공 모델명 — 프라이싱 비교표와 같은 단일 소스(constants/aiModels.js)를 쓴다
export const AI30_MODELS = [
  { id: 'gpt', name: 'GPT', model: AI_MODELS.gpt.latest },
  { id: 'claude', name: 'Claude', model: AI_MODELS.claude.latest },
  { id: 'gemini', name: 'Gemini', model: AI_MODELS.gemini.latest },
];

export const AI30_TASKS = [
  { id: 'saenggibu', name: '생기부', label: '최신 유료 AI' },
  { id: 'hwp', name: '한글 문서', label: '최신 유료 AI' },
  { id: 'ppt', name: '수업 PPT', label: '최신 유료 AI' },
  { id: 'worksheet', name: '학습지', label: '최신 유료 AI' },
];

// ── 이벤트 마감 (9/30 → 10/31 연장) ──
// 실제 마감(10/31)만 사실대로 표시한다 — 가짜 긴급성 금지.
// 종료 시 AI30_EVENT_ENABLED=false로 바꾸면 랜딩의 마감 배지·마감 FAQ·공유 마감
// 문구가 숨고, 랜딩과 쿠폰 자동지급 로직 자체는 유지된다. 배포가 늦어도
// isAi30EventActive()가 날짜(KST 자정) 기준으로 자동으로 꺼진다.
// 백엔드 클레임 차단은 AI30 쿠폰 문서(pro_30day_trial 그룹)의 valid_until이 담당
// (backend/scripts/ensure_ai30_coupon.py — 11/15 UTC, 마감 후 유예).
export const AI30_EVENT_ENABLED = true;
export const AI30_EVENT_END = '2026-10-31';
export const AI30_EVENT_END_LABEL = '10월 31일까지';

export const isAi30EventActive = (now = new Date()) => (
  AI30_EVENT_ENABLED && now <= new Date(`${AI30_EVENT_END}T23:59:59+09:00`)
);

export const AI30_SHARE_LANDING_URL = 'https://whatquiz.co.kr/events/2nd-semester-ai';

// 카카오 카드 이미지는 광고 소재(marketing/meta-ai30 ai30_base_1x1_v1) 기반 800×800.
// 카카오가 이미지 URL 단위로 캐시하므로 소재를 바꿀 땐 새 파일명으로 교체한다.
export const AI30_SHARE_MESSAGE = {
  title: '2학기 밀린 업무, AI로 미리 끝내요',
  text: '가입하면 30일 무료로 생기부·한글·PPT까지. 왓퀴즈 PRO',
  buttonTitle: '30일 무료로 시작하기',
  imageUrl: 'https://whatquiz.co.kr/images/events/og-ai30-2026q3.jpg',
  imageWidth: 800,
  imageHeight: 800,
};

// 이벤트 기간에만 마감을 얹은 공유 문구 — 종료 후 공유돼도 틀린 말이 남지 않게 한다.
export const getAi30ShareText = (now = new Date()) => (
  isAi30EventActive(now)
    ? `${AI30_EVENT_END_LABEL} 가입하면 30일 무료로 생기부·한글·PPT까지. 왓퀴즈 PRO`
    : AI30_SHARE_MESSAGE.text
);

// 교사 간 공유 링크. utm_source를 teacher_share로 고정해 ROAS 대시보드
// by_source에서 광고 유입과 분리 집계하고, 캠페인 키는 기존 기획과 동일하게 유지한다.
export const buildAi30ShareUrl = () => {
  const url = new URL(AI30_SHARE_LANDING_URL);
  url.searchParams.set('coupon', AI30_COUPON_CODE);
  url.searchParams.set('utm_source', 'teacher_share');
  url.searchParams.set('utm_medium', 'share');
  url.searchParams.set('utm_campaign', AI30_CAMPAIGN);
  url.searchParams.set('utm_content', 'share_button');
  return url.toString();
};

export const getAi30FirstUtm = () => {
  try {
    return getFirstUtmFromStorage() || {};
  } catch {
    return {};
  }
};

export const isAi30Campaign = (utm = getAi30FirstUtm()) => (
  String(utm.utm_campaign || '').trim().toLowerCase() === AI30_CAMPAIGN
);

export const capturePendingCouponFromUrl = (search = '') => {
  try {
    const params = new URLSearchParams(search);
    const coupon = (params.get('coupon') || '').trim().toUpperCase();
    if (!coupon) return '';
    // 기간 밖에 담아 두면 등록이 실패하면서 지워져, 기간 안에 다시 와도 자동 등록이 안 된다.
    if (coupon === FESTA_COUPON_CODE && getFestaPhase() !== FESTA_PHASES.LIVE) return '';
    localStorage.setItem(AI30_PENDING_COUPON_KEY, coupon);
    return coupon;
  } catch {
    return '';
  }
};

export const showAi30AppliedToastOnce = (showToast) => {
  if (typeof showToast !== 'function') return false;
  try {
    if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(AI30_GRANT_TOAST_KEY)) {
      return false;
    }
    sessionStorage.setItem(AI30_GRANT_TOAST_KEY, '1');
  } catch {
    // 세션 저장 실패해도 토스트는 1회 시도한다.
  }
  showToast('30일 PRO가 적용됐어요', 'success', 6000);
  return true;
};

export const maybeTrackAi30FirstTask = (feature = 'chat') => {
  try {
    if (typeof window === 'undefined') return false;
    if (sessionStorage.getItem(AI30_FIRST_TASK_FLAG)) return false;
    if (!isAi30Campaign()) return false;
    sessionStorage.setItem(AI30_FIRST_TASK_FLAG, '1');
    trackAi30Event('first_task_started', {
      stage: String(feature || 'chat').slice(0, 40),
    });
    return true;
  } catch {
    return false;
  }
};
